import { Avatar, Box, IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { PatientDetailDto } from '~/backend/connector-next-api/connector-next-schemas';
import { LabelText, ValueText } from '~/components/Fields/LabeledValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { SeparatorDot } from '~/components/Separated';
import {
    AssigneeAvatar,
    CourseStagePicker,
    CourseStageStrip,
    PatientIdText,
} from '../shared';
import {
    ageInYears,
    formatDayText,
    formatPatientName,
    initialsOf,
    patientAssignee,
    patientCourseStagesDone,
    resolveCourseStage,
    sexAgeText,
    type CourseStage,
} from '../model';

type DrilldownHeaderProps = {
    /** Undefined while the patient detail query is still loading. */
    patient: PatientDetailDto | undefined;
    onClose: () => void;
    /** Sets the patient's course stage and done-set; the header's only write. */
    onSetCourseStage: (change: {
        stage: CourseStage;
        done: readonly CourseStage[];
    }) => void;
    /** True while a course-stage write is in flight. */
    stageSaving: boolean;
};

/**
 * Panel header: patient identity, the course facts (stage, assignee, last
 * access, and the patient's notes when there are any), and the course stage
 * — where the course is, and which stages are done — which the user sets
 * here. The identity values and the notes render through the
 * hide-sensitive-info mechanism, initials included.
 */
export default function DrilldownHeader({
    patient,
    onClose,
    onSetCourseStage,
    stageSaving,
}: DrilldownHeaderProps) {
    const intl = useIntl();
    const name = formatPatientName(patient?.dicomPatientName);
    const stage = resolveCourseStage(patient?.courseStage);
    const stagesDone = patientCourseStagesDone(patient?.courseStagesDone);
    // A write needs the version to guard against; without one it is refused.
    const stageDisabled = stageSaving || patient?.version == null;
    const age = ageInYears(patient?.birthDate, new Date());
    const birthDay = formatDayText(intl, patient?.birthDate);
    const lastAccessed = formatDayText(intl, patient?.lastAccessedAt);
    const sexAge = sexAgeText(intl, patient?.sex, age);
    const assignee = patient ? patientAssignee(patient) : undefined;

    return (
        <Box
            sx={{
                px: 3,
                pt: 2.5,
                pb: 2,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Avatar
                    aria-hidden
                    sx={(theme) => ({
                        width: 48,
                        height: 48,
                        fontSize: 16,
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.primary.main, 0.18),
                        color: 'primary.light',
                    })}
                >
                    <SensitiveInfoTypography>
                        {name ? initialsOf(name) : ''}
                    </SensitiveInfoTypography>
                </Avatar>
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                    }}
                >
                    <Typography
                        component="span"
                        noWrap
                        sx={{ fontSize: 22, fontWeight: 600, lineHeight: 1.2 }}
                    >
                        <SensitiveInfoTypography>
                            {name || '…'}
                        </SensitiveInfoTypography>
                    </Typography>
                    {patient && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                fontFamily: 'fontFamilyMonospace',
                                fontSize: 13,
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                            }}
                        >
                            <PatientIdText id={patient.dicomPatientId} />
                            {sexAge && (
                                <>
                                    <SeparatorDot />
                                    <SensitiveInfoTypography>
                                        {sexAge}
                                    </SensitiveInfoTypography>
                                </>
                            )}
                            {birthDay && (
                                <>
                                    <SeparatorDot />
                                    <span>
                                        <FormattedMessage
                                            id="patientList.drilldown.dob"
                                            values={{
                                                date: (
                                                    <SensitiveInfoTypography>
                                                        {birthDay}
                                                    </SensitiveInfoTypography>
                                                ),
                                            }}
                                        />
                                    </span>
                                </>
                            )}
                        </Box>
                    )}
                </Box>
                <IconButton
                    size="small"
                    onClick={onClose}
                    aria-label={intl.formatMessage({
                        id: 'patientList.drilldown.close',
                    })}
                    title={intl.formatMessage({
                        id: 'patientList.drilldown.closeTitle',
                    })}
                    sx={{ color: 'text.secondary' }}
                >
                    <X size={18} />
                </IconButton>
            </Box>

            {patient && (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1.2fr 1fr 1fr',
                        gridTemplateRows: 'auto auto',
                        gridAutoFlow: 'column',
                        columnGap: 2,
                        rowGap: '2px',
                    }}
                >
                    <Fact
                        labelId="patientList.column.status"
                        value={
                            <CourseStagePicker
                                stage={stage}
                                done={stagesDone}
                                onChange={onSetCourseStage}
                                disabled={stageDisabled}
                            />
                        }
                    />
                    <Fact
                        labelId="patientList.column.assignee"
                        value={
                            assignee && (
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    <AssigneeAvatar name={assignee} />
                                    <Typography
                                        component="span"
                                        noWrap
                                        fontSize={14}
                                    >
                                        {assignee}
                                    </Typography>
                                </Box>
                            )
                        }
                    />
                    <Fact
                        labelId="patientList.drilldown.lastAccessed"
                        value={lastAccessed}
                        mono
                    />
                </Box>
            )}

            {patient?.notes && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                    }}
                >
                    <LabelText>
                        <FormattedMessage id="patientList.drilldown.fact.notes" />
                    </LabelText>
                    {/* Free text, so it may name anything about the patient:
                        hidden with the identity, and kept as it was typed. */}
                    <ValueText
                        component="span"
                        sx={{
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'anywhere',
                        }}
                    >
                        <SensitiveInfoTypography>
                            {patient.notes}
                        </SensitiveInfoTypography>
                    </ValueText>
                </Box>
            )}

            {patient && (
                <Box sx={{ pt: 0.5 }}>
                    <CourseStageStrip
                        stage={stage}
                        done={stagesDone}
                        size="large"
                        disabled={stageDisabled}
                        onToggleDone={(toggled) =>
                            onSetCourseStage({
                                stage,
                                done: stagesDone.includes(toggled)
                                    ? stagesDone.filter(
                                          (value) => value !== toggled
                                      )
                                    : [...stagesDone, toggled],
                            })
                        }
                    />
                </Box>
            )}
        </Box>
    );
}

/**
 * One labelled fact of the header grid, as its label and value cells; the
 * grid flows the label onto one row and the value onto the next, so a label
 * that wraps keeps every value aligned. An absent value renders as a dash.
 */
function Fact({
    labelId,
    value,
    mono = false,
}: {
    labelId: string;
    /** The fact, or nothing (undefined, null, '' or false) when the record holds none. */
    value: ReactNode;
    mono?: boolean;
}) {
    const present =
        value !== undefined &&
        value !== null &&
        value !== '' &&
        value !== false;
    return (
        <>
            <LabelText sx={{ whiteSpace: 'normal', alignSelf: 'end' }}>
                <FormattedMessage id={labelId} />
            </LabelText>
            <ValueText
                component="span"
                sx={{
                    minWidth: 0,
                    fontFamily: mono ? 'fontFamilyMonospace' : undefined,
                }}
            >
                {present ? value : '—'}
            </ValueText>
        </>
    );
}
